/**
 * V5 玩家进度存储 — 教学关卡进度跨会话持久化
 *
 * v5-003 §1.3 + v5-002 §2.4: 阶段锁定、已完成关卡、身份头衔、尝试次数。
 * localStorage key: "battlecode-v5-progress"
 */

import type { Phase } from "./api-surface-manager";
import { dataProxy } from "../core/data-proxy.js";

// ============================================================
// 类型定义
// ============================================================

export interface LevelStats {
  levelId: string;
  attempts: number;
  completed: boolean;
  firstCompletedAt?: number;
  bestTicks?: number;
}

export interface V5Progress {
  /** 当前解锁的最高阶段（0-4） */
  currentPhase: Phase;
  /** 已完成关卡 ID 列表 */
  completedLevels: string[];
  /** 身份头衔（基于已通过的阶段） */
  identityTitle: string;
  /** 总计尝试次数 */
  totalAttempts: number;
  /** 每关统计数据 */
  levelStats: Record<string, LevelStats>;
  /** 最近一次活动时间戳 */
  lastActiveAt: number;
  /** 数据版本号 */
  version: number;
}

// ============================================================
// 身份头衔映射
// ============================================================

const PHASE_TITLES: Record<number, string> = {
  0: "训练生",
  1: "战术新星",
  2: "逻辑编织者",
  3: "架构推演师",
  4: "演习先知",
};

// ============================================================
// localStorage 操作
// ============================================================

const CURRENT_VERSION = 1;

function getDefaultProgress(): V5Progress {
  return {
    currentPhase: 0,
    completedLevels: [],
    identityTitle: PHASE_TITLES[0],
    totalAttempts: 0,
    levelStats: {},
    lastActiveAt: Date.now(),
    version: CURRENT_VERSION,
  };
}

/** 加载玩家进度 */
export function loadProgress(): V5Progress {
  const parsed = dataProxy.get<V5Progress>("v5-progress");
  if (!parsed) return getDefaultProgress();
  // 版本迁移
  if (parsed.version !== CURRENT_VERSION) {
    return migrateProgress(parsed);
  }
  return parsed;
}

/** 保存玩家进度 */
export function saveProgress(progress: V5Progress): void {
  progress.lastActiveAt = Date.now();
  dataProxy.set("v5-progress", progress);
}

/** 版本迁移 */
function migrateProgress(old: any): V5Progress {
  // 目前仅 v1，后续版本在此添加迁移逻辑
  const def = getDefaultProgress();
  return {
    ...def,
    completedLevels: Array.isArray(old.completedLevels) ? old.completedLevels : [],
    currentPhase: typeof old.currentPhase === "number" ? old.currentPhase : 0,
    totalAttempts: typeof old.totalAttempts === "number" ? old.totalAttempts : 0,
    levelStats: typeof old.levelStats === "object" ? old.levelStats : {},
    version: CURRENT_VERSION,
  };
}

// ============================================================
// 进度操作
// ============================================================

/**
 * 记录一次关卡尝试（无论成败）。
 * 在屏 4 战斗结束时调用。
 */
export function recordAttempt(levelId: string): V5Progress {
  const progress = loadProgress();
  progress.totalAttempts++;

  if (!progress.levelStats[levelId]) {
    progress.levelStats[levelId] = { levelId, attempts: 0, completed: false };
  }
  progress.levelStats[levelId].attempts++;

  saveProgress(progress);
  return progress;
}

/**
 * 标记关卡为已完成。
 * 屏 4 战斗胜利时调用。
 *
 * @returns 更新后的进度和是否触发阶段晋级
 */
export function markLevelCompleted(
  levelId: string,
  stats?: { ticks?: number },
): { progress: V5Progress; phaseUnlocked: boolean; newPhase?: Phase } {
  const progress = loadProgress();

  // 标记完成
  if (!progress.completedLevels.includes(levelId)) {
    progress.completedLevels.push(levelId);
  }

  if (!progress.levelStats[levelId]) {
    progress.levelStats[levelId] = { levelId, attempts: 1, completed: false };
  }
  progress.levelStats[levelId].completed = true;
  progress.levelStats[levelId].firstCompletedAt = progress.levelStats[levelId].firstCompletedAt ?? Date.now();
  if (stats?.ticks !== undefined) {
    const prev = progress.levelStats[levelId].bestTicks;
    if (prev === undefined || stats.ticks < prev) {
      progress.levelStats[levelId].bestTicks = stats.ticks;
    }
  }

  // 检查阶段晋级：当前阶段所有关卡完成 → 晋级
  const completedInPhase = PHASE_LEVELS[progress.currentPhase]?.every((lid) =>
    progress.completedLevels.includes(lid),
  );

  let phaseUnlocked = false;
  let newPhase: Phase | undefined;

  if (completedInPhase && progress.currentPhase < 4) {
    newPhase = (progress.currentPhase + 1) as Phase;
    progress.currentPhase = newPhase;
    progress.identityTitle = PHASE_TITLES[newPhase];
    phaseUnlocked = true;
  }

  saveProgress(progress);
  return { progress, phaseUnlocked, newPhase };
}

/**
 * 检查指定关卡是否已解锁。
 * 解锁条件：阶段内前置关卡已完成 或 已是第一阶段。
 */
export function isLevelUnlocked(levelId: string): boolean {
  const progress = loadProgress();

  // 找到该关卡的阶段
  const phase = getLevelPhase(levelId);
  if (phase === undefined) return true; // 非 V5 关卡，无限制

  // 第一阶段无条件解锁
  if (phase === 0) return true;

  // 检查解锁条件
  const phaseLevels = PHASE_LEVELS[phase] ?? [];
  const idx = phaseLevels.indexOf(levelId);
  if (idx === -1) return true;

  // 同一阶段前置关卡全部完成
  for (let i = 0; i < idx; i++) {
    if (!progress.completedLevels.includes(phaseLevels[i])) {
      return false;
    }
  }

  return true;
}

/** 获取下一个已解锁但未完成的关卡 */
export function getNextUnlockedLevel(): string | null {
  const progress = loadProgress();

  for (let phase = 0; phase <= progress.currentPhase; phase++) {
    const levels = PHASE_LEVELS[phase] ?? [];
    for (const levelId of levels) {
      if (!progress.completedLevels.includes(levelId)) {
        return levelId;
      }
    }
  }

  return null; // 全部完成
}

/** 获取关卡所属阶段 */
export function getLevelPhase(levelId: string): Phase | undefined {
  for (let phase = 0; phase <= 4; phase++) {
    const levels = PHASE_LEVELS[phase] ?? [];
    if (levels.includes(levelId)) return phase as Phase;
  }
  return undefined;
}

// ============================================================
// 关卡 → 阶段映射表
// ============================================================

const PHASE_LEVELS: Record<number, string[]> = {
  0: ["v5_0-1_first_contact", "v5_0-2_find_enemy", "v5_0-3_moving_target"],
  1: ["v5_1-1_direction", "v5_1-2_cover_aware", "v5_1-3_cooldown"],
  2: ["v5_2-1_condition_basic", "v5_2-2_hp_gambit", "v5_2-3_multi_condition"],
  3: ["v5_3-1_scout_engage", "v5_3-2_state_switch", "v5_3-3_flank"],
  4: ["v5_4-1_full_plan", "v5_4-2_final_exercise"],
};
